import { useState, useCallback } from 'react';
import { getOrderDetails, updateOrderByShopifyId } from '../services';
import { UpdateOrder, ApiResponse, OrderResponse } from '../types';

/**
 * Hook personalizado para gestionar operaciones relacionadas con órdenes
 * Proporciona funciones para obtener y actualizar órdenes, junto con estados de carga y error
 */
export const useOrderService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Obtiene los detalles de una orden por su ID
   * @param orderId - ID de la orden en Shopify
   * @returns Promesa con los datos de la orden o undefined en caso de error
   */
  const fetchOrderDetails = useCallback(async (orderId: string): Promise<OrderResponse | undefined> => {
    if (!orderId) return undefined;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getOrderDetails(orderId);
      return {
        data: response.data,
        delivery: response.delivery
      };
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al obtener los detalles de la orden';
      setError(errorMessage);
      console.error('Error en useOrderService:', errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualiza la dirección de envío de una orden
   * @param shopifyOrderId - ID de la orden en Shopify
   * @param shippingAddress - Datos actualizados de la dirección de envío
   * @returns Promesa con la respuesta de la API
   */
  const updateOrder = useCallback(async (shopifyOrderId: string, shippingAddress: UpdateOrder): Promise<ApiResponse | undefined> => {
    if (!shopifyOrderId) {
      setError('ID de orden no proporcionado');
      return undefined;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await updateOrderByShopifyId(shopifyOrderId, shippingAddress);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al actualizar la orden';
      setError(errorMessage);
      console.error('Error en useOrderService:', errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    fetchOrderDetails,
    updateOrder,
    loading,
    error,
  };
}; 